import { httpGet, httpPost } from '../lib/http';

/**
 * Subject Practice Service
 * Handles all API calls for subject-wise topic practice feature using optimized syllabus endpoints
 */

// Get complete syllabus with user progress if userId is provided
export const getSyllabusWithProgress = async (userId = null) => {
  const endpoint = userId 
    ? `/api/syllabus?userId=${userId}`
    : '/api/syllabus';
  const response = await httpGet(endpoint);
  return await response.json();
};

// Get detailed subject info with user progress
export const getSubjectWithProgress = async (subjectId, userId = null) => {
  const endpoint = userId 
    ? `/api/syllabus/${subjectId}?userId=${userId}`
    : `/api/syllabus/${subjectId}`;
  const response = await httpGet(endpoint);
  return await response.json();
};

export const updateConceptProgress = async (userId, conceptId, progressData) => {
  return await httpPost(`/api/users/${userId}/progress/concepts/${conceptId}`, progressData);
};

export const updateSubtopicProgress = async (userId, subtopicId, progressData) => {
  return await httpPost(`/api/users/${userId}/progress/subtopics/${subtopicId}`, progressData);
};

export const updateTopicProgress = async (userId, topicId, progressData) => {
  return await httpPost(`/api/users/${userId}/progress/topics/${topicId}`, progressData);
};

export const updateSubjectProgress = async (userId, subjectId, progressData) => {
  return await httpPost(`/api/users/${userId}/progress/subjects/${subjectId}`, progressData);
};

// Practice Session APIs
export const startPracticeSession = async (conceptId, sessionConfig = {}) => {
  return await httpPost('/api/practice/sessions/start', {
    conceptId,
    ...sessionConfig
  });
};

export const submitAnswer = async (sessionId, questionId, answerData) => {
  return await httpPost(`/api/practice/sessions/${sessionId}/answer`, {
    questionId,
    ...answerData
  });
};

export const completePracticeSession = async (sessionId, sessionData) => {
  return await httpPost(`/api/practice/sessions/${sessionId}/complete`, sessionData);
};

// Recommendations API
export const getUserRecommendations = async (userId) => {
  return await httpGet(`/api/users/${userId}/recommendations`);
};

// Utility functions for data processing
export const normalizeSubject = (subject) => ({
  id: subject.id,
  name: subject.name,
  description: subject.description,
  icon: subject.icon || 'calculate',
  color: subject.color || '#2196F3',
  totalTopics: subject.totalTopics || 0,
  completedTopics: subject.completedTopics || 0,
  progress: subject.progress || 0,
  estimatedHours: subject.estimatedHours || 0,
  difficulty: subject.difficulty || 'intermediate',
  topics: subject.topics || []
});

export const normalizeTopic = (topic) => ({
  id: topic.id,
  name: topic.name,
  subjectId: topic.subjectId,
  description: topic.description,
  icon: topic.icon || 'functions',
  color: topic.color || '#4CAF50',
  totalSubtopics: topic.totalSubtopics || 0,
  completedSubtopics: topic.completedSubtopics || 0,
  progress: topic.progress || 0,
  estimatedHours: topic.estimatedHours || 0,
  difficulty: topic.difficulty || 'beginner',
  prerequisites: topic.prerequisites || [],
  subtopics: topic.subtopics || []
});

export const normalizeSubtopic = (subtopic) => ({
  id: subtopic.id,
  name: subtopic.name,
  topicId: subtopic.topicId,
  subjectId: subtopic.subjectId,
  description: subtopic.description,
  icon: subtopic.icon || 'timeline',
  color: subtopic.color || '#FF9800',
  totalConcepts: subtopic.totalConcepts || 0,
  completedConcepts: subtopic.completedConcepts || 0,
  progress: subtopic.progress || 0,
  estimatedHours: subtopic.estimatedHours || 0,
  difficulty: subtopic.difficulty || 'beginner',
  concepts: subtopic.concepts || []
});

export const normalizeConcept = (concept) => ({
  id: concept.id,
  name: concept.name,
  subtopicId: concept.subtopicId,
  topicId: concept.topicId,
  subjectId: concept.subjectId,
  description: concept.description,
  content: concept.content || {},
  userProgress: concept.userProgress || {
    status: 'not_started',
    score: 0,
    attempts: 0,
    timeSpent: 0,
    lastAccessed: null,
    completionDate: null,
    masteryLevel: 0
  }
});

// Progress status utilities
export const PROGRESS_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  MASTERED: 'mastered'
};

// Helper functions for data display
export const getProgressColor = (progress) => {
  if (progress >= 90) return '#4CAF50'; // Green
  if (progress >= 70) return '#8BC34A'; // Light Green
  if (progress >= 50) return '#FFC107'; // Yellow
  if (progress >= 25) return '#FF9800'; // Orange
  return '#F44336'; // Red
};

export const getDifficultyColor = (difficulty) => {
  switch (difficulty?.toLowerCase()) {
    case 'beginner': return 'success';
    case 'easy': return 'success';
    case 'intermediate': return 'warning';
    case 'medium': return 'warning';
    case 'advanced': return 'error';
    case 'hard': return 'error';
    default: return 'default';
  }
};

export const calculateSubjectProgress = (topics) => {
  if (!topics || topics.length === 0) return 0;
  const totalProgress = topics.reduce((sum, topic) => sum + (topic.progress || 0), 0);
  return Math.round(totalProgress / topics.length);
};

export const calculateTopicProgress = (subtopics) => {
  if (!subtopics || subtopics.length === 0) return 0;
  const totalProgress = subtopics.reduce((sum, subtopic) => sum + (subtopic.progress || 0), 0);
  return Math.round(totalProgress / subtopics.length);
};

export const calculateSubtopicProgress = (concepts) => {
  if (!concepts || concepts.length === 0) return 0;
  const totalProgress = concepts.reduce((sum, concept) => {
    const progress = concept.userProgress?.status === PROGRESS_STATUS.COMPLETED ? 100 :
                    concept.userProgress?.status === PROGRESS_STATUS.IN_PROGRESS ? 50 : 0;
    return sum + progress;
  }, 0);
  return Math.round(totalProgress / concepts.length);
};